interface SkeletonProps {
  className?: string;
}

/**
 * F-0604 WAIT-20: the one placeholder bar. `.evx-skeleton` (`index.css`) is a slow brand-toned breath
 * between two tones that read on the light and the dark ground, at rest under Reduce Motion. A site's
 * own box that is not a `Skeleton` takes the class, never its own pulse (`skeleton.guard.test.ts`).
 */
export function Skeleton({ className = '' }: SkeletonProps) {
  return <div className={`evx-skeleton rounded ${className}`} />;
}

/**
 * Placeholder in the shape of the row it becomes — the row's height, the row's padding, and the
 * tracks the row's content sits in — so swapping in the real rows never shifts the layout.
 *
 * That sentence stood above this component before, as a claim about height alone, and it was true
 * at every width anybody had opened the list at. Measured against `WorkItemListRow` at 1440, 768
 * and 390 it was wrong five ways, four of them at every width (I-0247):
 *
 * - The box was `h-9 px-3` flat where the row is `h-12 px-2 sm:h-9 sm:px-3`. On a phone the
 *   placeholder was 36px and the row 48px, so every row and every heading below it dropped 12px
 *   when the answer arrived — on the one viewport where the list is most of the screen.
 * - Nothing reserved the row's collapse control (`mr-1 h-5 w-5`), so the status dot and everything
 *   after it began 24px left of where it lands: **300 against 324** at 1440, 36 against 60 at 768,
 *   24 against 44 at 390. The item that filed this read the two as horizontally exact, which the
 *   *container* edges are — 12 and 374 on both — and the content is not.
 * - The content gap was `gap-3` flat where the row's is `gap-2 sm:gap-3`.
 * - The identifier block was `w-14` against the row's `w-[52px] sm:w-[58px]` track.
 * - The trailing pills were drawn at every width, and below `sm` the row has no trailing cell at
 *   all — every one of them is `sm:`, `md:` or `lg:`.
 *
 * The trailing cells are the one part not copied track for track. Which of them a reader has
 * turned on is `WorkItemsPage`'s to know and this component's not, and reserving five per-column
 * widths here would be a second copy of the row's trailing region drifting against the first. What
 * holds whatever the reader picked is the breakpoint, so that is what is copied: nothing below
 * `sm`, and above it a pair standing in for whatever is on — which moves where the title truncates
 * and not where any row sits.
 */
export function WorkItemRowSkeleton({ widthClass = 'w-2/3' }: { widthClass?: string }) {
  return (
    // `data-coarse-row`: 44 px with a coarse pointer, as the row it stands for is (I-1286).
    <div
      data-list-wait="row"
      data-coarse-row=""
      className="flex h-12 items-center px-2 sm:h-9 sm:px-3"
    >
      {/* The collapse control's slot. It holds no bar because a placeholder should not promise a
          control that only some rows get, but it has to hold the space, or every row's content
          slides 24px right the moment the list answers. */}
      <span className="mr-1 h-5 w-5 shrink-0" />
      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
        <Skeleton className="h-2 w-2 rounded-full" />
        <Skeleton className="h-3 w-[52px] sm:w-[58px]" />
        <Skeleton className={`h-3 ${widthClass}`} />
        <div className="ml-auto hidden items-center gap-2 sm:flex">
          <Skeleton className="h-4 w-14 rounded-full" />
          <Skeleton className="h-4 w-16 rounded-full" />
        </div>
      </div>
    </div>
  );
}

/**
 * A grouped run of row skeletons with a section-header placeholder.
 *
 * The heading is `max-sm:min-h-touch px-3 gap-2 sm:h-7` — the collapsing control's own box: 44px on a phone, where
 * the control became a 44px target (F-0231), and 28px from `sm` up. The placeholder kept the 28px
 * alone, so on a phone every section dropped 16px when the list answered. Before that, it drew a
 * bare content row plus `pb-2` and came to 18px. That one was wrong at 1440 and 768
 * as well as on the phone, so each of the two sections started 10px above where its rows land
 * (I-0247). Its three blocks stand at the three things the heading actually draws: the chevron,
 * the status dot, and the label.
 */
export function WorkItemListSkeleton() {
  const groups = [
    ['w-2/5', 'w-1/2', 'w-1/3'],
    ['w-1/2', 'w-2/5', 'w-3/5', 'w-1/3'],
  ];
  return (
    // I-1286: with a coarse pointer the list's group headers and rows are 44 px (`index.css`), and
    // so is what waits in their place, or every row moves when the list answers.
    <div className="space-y-6" aria-hidden="true" data-coarse-targets="">
      {groups.map((rows, groupIndex) => (
        <div key={groupIndex}>
          <div
            data-list-wait="heading"
            data-coarse-row=""
            className="flex max-sm:min-h-touch items-center gap-2 px-3 sm:h-7"
          >
            <Skeleton className="h-3.5 w-3.5 shrink-0" />
            <Skeleton className="h-1.5 w-1.5 shrink-0 rounded-full" />
            <Skeleton className="h-2.5 w-20" />
          </div>
          <div className="space-y-0.5">
            {rows.map((widthClass, rowIndex) => (
              <WorkItemRowSkeleton key={rowIndex} widthClass={widthClass} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
